/**
 * Collection Bundle Builder
 *
 * Integrates with existing quick-shop UI to build bundles on collection pages.
 * Uses inline variant selection from quick-shop, adds to mini-cart widget instead of cart.
 */

(function() {
  'use strict';

  // ==========================================
  // State Management
  // ==========================================

  const BundleState = {
    selectedItems: [],
    config: null,
    widget: null,
    maxItems: 4,
    isInitialized: false
  };

  // ==========================================
  // Initialization
  // ==========================================

  function init() {
    // Check if bundle builder is enabled
    if (!window.foxsell?.collectionBundleConfig?.enabled) {
      return;
    }

    BundleState.config = window.foxsell.collectionBundleConfig;
    BundleState.maxItems = BundleState.config.bundleQuantity || 4;

    // Cache DOM elements
    BundleState.widget = {
      container: document.getElementById('collection-bundle-widget'),
      overlay: document.getElementById('collection-bundle-overlay'),
      progressCurrent: document.getElementById('bundle-progress-current'),
      itemsContainer: document.getElementById('bundle-widget-items'),
      completeButton: document.getElementById('complete-bundle-button'),
      closeButton: document.getElementById('close-bundle-widget')
    };

    // Set up event listeners
    setupEventListeners();

    // Set grid columns dynamically based on bundle quantity
    if (BundleState.widget.itemsContainer && BundleState.maxItems) {
      BundleState.widget.itemsContainer.style.gridTemplateColumns = `repeat(${BundleState.maxItems}, minmax(140px, 180px))`;
    }

    // Mark bundle mode as active (for quick-shop.js to detect)
    window.bundleModeActive = true;

    // Mark as initialized
    BundleState.isInitialized = true;

    // console.log('[Collection Bundler] Initialized', BundleState.config);
  }

  // ==========================================
  // Event Listeners
  // ==========================================

  function setupEventListeners() {
    // Complete bundle button
    if (BundleState.widget.completeButton) {
      BundleState.widget.completeButton.addEventListener('click', handleCompleteBundle);
    }

    // Close widget button - toggle on both desktop and mobile
    if (BundleState.widget.closeButton) {
      BundleState.widget.closeButton.addEventListener('click', function() {
        toggleWidget(); // Use same toggle behavior for both mobile and desktop
      });
    }

    // Close widget when clicking overlay (mobile only)
    if (BundleState.widget.overlay) {
      BundleState.widget.overlay.addEventListener('click', hideWidget);
    }

    // Expose global handler for quick-shop integration
    window.addToBundleCart = addItemToBundle;
  }

  // ==========================================
  // Bundle Management
  // ==========================================

  /**
   * Add item to bundle (called from quick-shop.js)
   * @param {number} variantId - Selected variant ID
   * @param {Object} productData - Product information from quick-shop
   */
  function addItemToBundle(variantId, productData) {
    // Check if bundle is full
    if (BundleState.selectedItems.length >= BundleState.maxItems) {
      alert(`You can only select ${BundleState.maxItems} items for this bundle.`);
      return;
    }

    // console.log('[Collection Bundler] Adding item', { variantId, productData });

    // Add item to state
    BundleState.selectedItems.push({
      variantId: variantId,
      productId: productData.productId || '',
      productTitle: productData.productTitle || '',
      productHandle: productData.productHandle || '',
      variantTitle: productData.variantTitle || '',
      price: productData.price || 0,
      image: productData.image || '',
      selectedOptions: productData.selectedOptions || {}
    });

    // Update UI
    updateWidgetDisplay();
    showWidget();
  }

  function removeItemFromBundle(index) {
    BundleState.selectedItems.splice(index, 1);
    updateWidgetDisplay();

    // Hide widget if empty
    if (BundleState.selectedItems.length === 0) {
      hideWidget();
    }
  }

  function updateWidgetDisplay() {
    // Update progress count
    if (BundleState.widget.progressCurrent) {
      BundleState.widget.progressCurrent.textContent = BundleState.selectedItems.length;
    }

    // Update items display
    if (BundleState.widget.itemsContainer) {
      let itemsHTML = '';

      // Show selected items
      BundleState.selectedItems.forEach((item, index) => {
        const optionsText = Object.values(item.selectedOptions).join(', ');
        itemsHTML += `
          <div class="collection-bundle-widget__item" data-item-index="${index}">
            <div class="collection-bundle-widget__item-image-wrapper">
              <img
                src="${item.image}"
                alt="${item.productTitle}"
                class="collection-bundle-widget__item-thumbnail"
              >
              <button
                class="collection-bundle-widget__item-remove"
                data-item-index="${index}"
                aria-label="Remove ${item.productTitle}"
              >
                ×
              </button>
            </div>
            <div class="collection-bundle-widget__item-info">
              <div class="collection-bundle-widget__item-title">${item.productTitle}</div>
              <div class="collection-bundle-widget__item-variant">${optionsText}</div>
            </div>
          </div>
        `;
      });

      // Add placeholders for remaining slots
      const remainingSlots = BundleState.maxItems - BundleState.selectedItems.length;
      for (let i = 0; i < remainingSlots; i++) {
        itemsHTML += '<div class="collection-bundle-widget__item-placeholder">+</div>';
      }

      BundleState.widget.itemsContainer.innerHTML = itemsHTML;

      // Add remove button listeners
      const removeButtons = BundleState.widget.itemsContainer.querySelectorAll('.collection-bundle-widget__item-remove');
      removeButtons.forEach(button => {
        button.addEventListener('click', function() {
          const index = parseInt(this.getAttribute('data-item-index'));
          removeItemFromBundle(index);
        });
      });
    }

    // Update complete button state
    if (BundleState.widget.completeButton) {
      BundleState.widget.completeButton.disabled = BundleState.selectedItems.length !== BundleState.maxItems;
    }
  }

  function showWidget() {
    if (BundleState.widget.container) {
      BundleState.widget.container.classList.add('active');
    }

    // Show overlay on mobile
    if (BundleState.widget.overlay) {
      BundleState.widget.overlay.classList.add('active');
    }
  }

  function toggleWidget() {
    if (BundleState.widget.container) {
      const isActive = BundleState.widget.container.classList.contains('active');

      if (isActive) {
        // Currently showing - hide it
        BundleState.widget.container.classList.remove('active');

        // Hide overlay (mobile)
        if (BundleState.widget.overlay) {
          BundleState.widget.overlay.classList.remove('active');
        }
      } else {
        // Currently hidden - show it
        showWidget();
      }
    }
  }

  function hideWidget() {
    if (BundleState.widget.container) {
      BundleState.widget.container.classList.remove('active');
    }

    // Hide overlay (mobile only)
    if (BundleState.widget.overlay) {
      BundleState.widget.overlay.classList.remove('active');
    }
  }

  // ==========================================
  // Bundle Completion & Cart Add
  // ==========================================

  async function handleCompleteBundle() {
    if (BundleState.selectedItems.length !== BundleState.maxItems) {
      alert(`Please select ${BundleState.maxItems} items to complete your bundle.`);
      return;
    }

    // Disable button to prevent double-clicks
    BundleState.widget.completeButton.disabled = true;
    BundleState.widget.completeButton.textContent = 'Adding to cart...';

    try {
      const bundlePayload = buildBundlePayload();
      await addBundleToCart(bundlePayload);

      // Success - reset bundle state
      BundleState.selectedItems = [];
      updateWidgetDisplay();
      hideWidget();

      // Show success message
      showSuccessNotification();

    } catch (error) {
      console.error('[Collection Bundler] Error adding bundle to cart', error);
      alert('There was an error adding your bundle to the cart. Please try again.');
    } finally {
      // Re-enable button
      BundleState.widget.completeButton.disabled = false;
      BundleState.widget.completeButton.textContent = 'Complete Bundle';
    }
  }

  function buildBundlePayload() {
    const bundleId = BundleState.config.bundleId || `collection_bundle_${Date.now()}`;
    const bundleTitle = BundleState.config.bundleTitle || 'Bundle';

    // Build Foxsell-compatible line item properties (matching product-bundler.js format)
    const bundleLineItemProperties = BundleState.selectedItems.map((item, index) => {
      // Build properties object with selected options
      const properties = {
        ...item.selectedOptions,
        '_placement': `item ${index + 1}`,
        '_Bundle': bundleId,
        'Part of': bundleTitle
      };

      return {
        variantId: item.variantId.toString(),
        quantity: 1,
        category: 'Bundle Item',
        type: 'product',
        title: item.productTitle,  // Add product title here
        price: item.price,
        image: item.image,
        properties: properties
      };
    });

    // Build simplified bundle item details for Rebuy cart display
    // This matches the format from product-bundler.js lines 542-560
    const bundleItemDetails = BundleState.selectedItems.map(item => {
      // Filter out internal properties for display purposes
      const variantOptions = {};

      Object.entries(item.selectedOptions).forEach(([key, value]) => {
        // Exclude internal properties (those starting with _ or known system properties)
        if (!key.startsWith('_') && key !== 'stepCategory' && key !== 'Part of') {
          variantOptions[key] = value;
        }
      });

      // Build formatted variant title (e.g., "Sterling Silver / 18"")
      const variantTitle = Object.values(variantOptions).join(' / ');

      return {
        title: item.productTitle,
        variantTitle: variantTitle
      };
    });

    // console.log('[Collection Bundler] Bundle line item properties:', bundleLineItemProperties);
    // console.log('[Collection Bundler] Bundle item details for Rebuy:', bundleItemDetails);

    // Build cart payload
    return {
      id: BundleState.config.bundleVariantId,
      quantity: 1,
      properties: {
        '__foxsell:dynamic_add_on_bundle_id': `${bundleId}_${Date.now()}`,
        '__foxsell:dynamic_add_on_bundle_items': JSON.stringify(bundleLineItemProperties),
        '__bundle_item_details': JSON.stringify(bundleItemDetails),
        '_custom_product': 'True'
      }
    };
  }

  async function addBundleToCart(payload) {
    // Build request body with sections parameter (matching product-bundler.js)
    const sectionsToRender = getSectionsToRender();
    const requestBody = {
      items: [payload],
      sections: sectionsToRender.map(section => section.section),
      sections_url: window.location.pathname
    };

    // console.log('[Collection Bundler] Adding to cart with payload:', requestBody);

    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      throw new Error(`Cart add failed: ${response.status}`);
    }

    const parsedState = await response.json();
    // console.log('[Collection Bundler] Bundle added to cart', parsedState);

    // Trigger cart update (similar to quick-shop.js)
    if (typeof updateShippingThreshold === 'function') {
      updateShippingThreshold();
    }

    // Update cart sections with the response that includes sections HTML
    if (parsedState.sections) {
      updateCartSections(parsedState);
    }

    return parsedState;
  }

  function showSuccessNotification() {
    // Try to use existing cart notification
    const cartNotification = document.getElementById('cart-notification');
    if (cartNotification) {
      window.scrollTo(0, 0);
      cartNotification.focus();
      cartNotification.classList.add('active');
    }

    // Also show mini-cart if available
    const miniCart = document.getElementById('shopify-section-mini-cart');
    if (miniCart) {
      const miniCartContainer = miniCart.querySelector('.mini-cart-container');
      if (miniCartContainer) {
        miniCartContainer.classList.add('not-empty');
      }
    }
  }

  // ==========================================
  // Cart Update Functions
  // ==========================================

  function findSectionInnerHTML(html, selector) {
    if (!html) return '';
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const element = doc.querySelector(selector);
    return element ? element.innerHTML : '';
  }

  function getSectionsToRender() {
    const sections = [];

    // Only add sections that exist on the page
    if (document.getElementById('main-cart-items')) {
      sections.push({
        id: 'main-cart-items',
        section: document.getElementById('main-cart-items').dataset.id,
        selector: '.js-contents',
      });
    }

    if (document.getElementById('cart-icon-bubble')) {
      sections.push({
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section'
      });
    }

    if (document.getElementById('cart-live-region-text')) {
      sections.push({
        id: 'cart-live-region-text',
        section: 'cart-live-region-text',
        selector: '.shopify-section'
      });
    }

    if (document.getElementById('main-cart-footer')) {
      sections.push({
        id: 'main-cart-footer',
        section: document.getElementById('main-cart-footer').dataset.id,
        selector: '.js-contents',
      });
    }

    return sections;
  }

  function updateCartSections(parsedState) {
    getSectionsToRender().forEach((section) => {
      const elementToReplace = document.getElementById(section.id)?.querySelector(section.selector);
      if (!elementToReplace) {
        console.warn(`[Collection Bundler] Element not found for id: ${section.id}`);
        return;
      }

      const innerHTMLValue = findSectionInnerHTML(parsedState.sections[section.section], section.selector);
      if (innerHTMLValue) {
        elementToReplace.innerHTML = innerHTMLValue;
      }
    });

    // Update cart header/footer if function exists
    if (typeof updateCartHeaderFooter === 'function') {
      updateCartHeaderFooter();
    }
  }

  // ==========================================
  // Utility Functions
  // ==========================================

  function formatMoney(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(cents / 100);
  }

  // ==========================================
  // Initialize on DOM Ready
  // ==========================================

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Expose for debugging
  window.CollectionBundler = {
    state: BundleState,
    addItem: addItemToBundle
  };

})();
